import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

export type PreferencesTab = 'email' | 'sms';

type Props = {
  current: PreferencesTab;
  emailPreferencesUrl: string;
  smsPreferencesUrl: string;
};

/**
 * Email / Phone, as links rather than panels.
 *
 * The two halves of notification preferences are two pages — email preferences
 * is still served by customer.php and this one is a route — so switching is a
 * navigation, and anchors are what a navigation should be. It also means the tab
 * bar works with JavaScript off and each tab can be linked to directly.
 *
 * Styled to match the panel tabs on the auto renewals page so the account area
 * reads as one thing; see AutoRenewals.tsx.
 *
 * Two things about the border are load-bearing. `border-solid` is written out
 * because Tailwind's preflight is not in the V2 stylesheet — see postcss/scopeV2
 * — so border-style is `none` until a utility says otherwise and a width alone
 * draws nothing. And the colour is set in each branch rather than defaulted to
 * transparent and overridden, because `border-transparent` and `border-slate-200`
 * have the same specificity and transparent is emitted last, so it wins wherever
 * both are applied no matter which order they are written in here.
 */
export default function PreferencesTabs({ current, emailPreferencesUrl, smsPreferencesUrl }: Props) {
  const { buildUrl } = useUrlGenerator();

  // The id names the page, the label names what the customer is managing there.
  // They differ on the second tab on purpose: the page is the phone number, and
  // texting is one thing you can ask us to do with it rather than the point of
  // the page — while the route, the props and this component's own `current`
  // all still say sms.
  const tabs: { id: PreferencesTab; label: string; href: string }[] = [
    { id: 'email', label: 'Email', href: emailPreferencesUrl },
    { id: 'sms', label: 'Phone', href: smsPreferencesUrl },
  ];

  return (
    <nav aria-label="Notifications" className="flex border-b-[1.5px] border-solid border-slate-200 mb-6">
      {tabs.map((tab) => {
        const selected = tab.id === current;

        return (
          <a
            key={tab.id}
            href={buildUrl(tab.href)}
            aria-current={selected ? 'page' : undefined}
            className={cn(
              'block p-1 pt-0 pb-1.5 group font-semibold no-underline',
              'bg-transparent border-solid border-t-[1.5px] border-x-[1.5px] border-b-0 -mb-[1.5px] rounded-t',
              selected
                ? 'bg-white text-slate-900 border-slate-200'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            )}
          >
            <span className={cn('block px-5 py-2.5 rounded', !selected && 'group-hover:bg-slate-100')}>
              {tab.label}
            </span>
          </a>
        );
      })}
    </nav>
  );
}
